import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import Constants from '../../../../assests/Constants/constants';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import Swal from 'sweetalert2';
import {
  PanelTableContainer as Container,
  VatPanelHeadCell as HeadCell,
  PanelBodyRow as BodyRow,
  PanelNumberCell as NumberCell,
  PanelStatusBadge as Badge,
  softPanelTableSx,
} from './panelTableStyles';


/* ── main component ─────────────────────────────────────────────── */

const AdvancesTable = ({
  direction = 'ltr',
  headerStyle,
  t,
  client,
  calculatedValues,
  witholdingIncomesNoVat,
  downloadWithholdingTaxReport,
  advanceReportTax,
  permissionsState,
  userTypeContext,
  handleOpenWithholdingTaxModal,
  partners = [],
  selectedPartnerId = 'all',
  partnerCalculatedValues = {},
  isSubordinateClient = false,
  mainClientData = null,
  isMainWithSubordinates = false,
  subordinateClients = [],
}) => {
  const {
    pageIncomeTaxAdvancePercentage,
    pageUseIncomeTaxAdvancePercentage,
    pageIncomeTaxAdvanceAmount,
    partnerAdvanceSettings,
  } = useSelector((state) => state.clientDataSlice);

  /** Matches ClientDetails / GetVatAdvancesPageData resolver when page data is loaded */
  const displayUsePercentage =
    pageUseIncomeTaxAdvancePercentage != null
      ? !!pageUseIncomeTaxAdvancePercentage
      : !!client?.useIncomeTaxAdvancePercentage;
  const displayAdvancePercentage =
    pageIncomeTaxAdvancePercentage != null && pageIncomeTaxAdvancePercentage !== ''
      ? Number(pageIncomeTaxAdvancePercentage)
      : Number(client?.incomeTaxAdvancePercentage) || 0;
  const displayAdvanceFixed =
    pageIncomeTaxAdvanceAmount != null && pageIncomeTaxAdvanceAmount !== ''
      ? Number(pageIncomeTaxAdvanceAmount)
      : Number(client?.incomeTaxAdvanceAmount) || 0;

  /**
   * Resolve partner-level yearly advance settings (from API), falling back to legacy partner fields,
   * then to client-level yearly settings if both are missing/zero. Mirrors backend resolver.
   */
  const getPartnerAdvanceDisplay = (partner) => {
    const yearly = partnerAdvanceSettings?.[partner?.id];

    const usePercentage =
      yearly?.useIncomeTaxAdvancePercentage != null
        ? !!yearly.useIncomeTaxAdvancePercentage
        : (partner?.useIncomeTaxAdvancePercentage ?? displayUsePercentage ?? true);

    const percentage =
      yearly?.incomeTaxAdvancePercentage != null
        ? Number(yearly.incomeTaxAdvancePercentage)
        : (Number(partner?.withholdingTaxPercentage) || displayAdvancePercentage || 0);

    const fixedAmount =
      yearly?.incomeTaxAdvanceAmount != null
        ? Number(yearly.incomeTaxAdvanceAmount)
        : (Number(partner?.incomeTaxAdvanceAmount) || displayAdvanceFixed || 0);

    return { usePercentage, percentage, fixedAmount };
  };

  // Get the selected partner or null if "all" is selected
  const selectedPartner = selectedPartnerId === 'all' ? null : partners.find(p => p.id === selectedPartnerId);

  // Function to handle partner selection for download when showing all partners
  const handleDownloadWithPartnerSelection = async () => {
    if (client.isPartners && partners.length > 0) {
      // Show partner selection dialog
      const result = await Swal.fire({
        title: t('SelectPartner'),
        html: `
          <select id="partner-select" style="width: 100%; padding: 10px; font-size: 14px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; max-width: 100%; margin: 10px 0;">
            ${partners.map((p, index) => `<option value="${p.id}" ${index === 0 ? 'selected' : ''}>${p.name} (${p.equity}%)</option>`).join('')}
          </select>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('Download'),
        cancelButtonText: t('Cancel'),
        width: '380px',
        padding: '1.5em 1.5em 1em',
        customClass: {
          container: 'swal2-container-no-scroll',
          popup: 'swal2-popup-compact'
        },
        didOpen: () => {
          // Prevent horizontal scrolling
          const container = document.querySelector('.swal2-container-no-scroll');
          if (container) {
            container.style.overflowX = 'hidden';
            container.style.maxWidth = '100vw';
          }
          const popup = document.querySelector('.swal2-popup-compact');
          if (popup) {
            popup.style.maxWidth = '380px';
            popup.style.overflowX = 'hidden';
          }
          const htmlContainer = document.querySelector('.swal2-html-container');
          if (htmlContainer) {
            htmlContainer.style.overflowX = 'hidden';
            htmlContainer.style.maxWidth = '100%';
          }

          // Ensure select has a value selected
          const select = document.getElementById('partner-select');
          if (select && !select.value && partners.length > 0) {
            select.value = partners[0].id;
          }
        },
        preConfirm: () => {
          const select = document.getElementById('partner-select');
          if (!select) {
            console.error('Select element not found!');
            Swal.showValidationMessage(t('PleaseSelectPartner'));
            return false;
          }
          const partnerId = select.value;

          if (!partnerId || partnerId === '') {
            console.error('No partnerId value found in select!');
            Swal.showValidationMessage(t('PleaseSelectPartner'));
            return false;
          }

          // Return the partner ID as string (GUID)
          return partnerId;
        },
        allowOutsideClick: false,
        allowEscapeKey: true
      });

      // Check if user confirmed and got a value
      if (result.isConfirmed) {
        let selectedPartnerId = result.value;

        // Fallback: if result.value is not available, get it directly from the select element
        if (!selectedPartnerId) {
          const select = document.getElementById('partner-select');
          if (select && select.value) {
            selectedPartnerId = select.value;
          }
        }

        if (selectedPartnerId) {
          // Ensure partnerId is passed as string (GUID format)
          downloadWithholdingTaxReport(selectedPartnerId);
        } else {
          console.error('No partnerId value found!');
          Swal.fire({
            icon: 'error',
            title: t('Error'),
            text: t('PleaseSelectPartner')
          });
        }
      } else {
        console.log('User cancelled the dialog');
      }
    } else {
      downloadWithholdingTaxReport(null);
    }
  };

  const isPartnerTable = Boolean(client?.isPartners && partners.length > 0);

  /** Hide the advance % / fixed-amount setting column when the client uses a fixed advance. */
  const showAdvancePercentageColumn = isPartnerTable
    ? (selectedPartner
        ? getPartnerAdvanceDisplay(selectedPartner).usePercentage
        : (displayUsePercentage || partners.some((p) => getPartnerAdvanceDisplay(p).usePercentage)))
    : displayUsePercentage;

  const tableSx = {
    ...softPanelTableSx,
    tableLayout: isPartnerTable ? 'fixed' : 'auto',
    minWidth: isPartnerTable ? '100%' : 400,
    ...(isPartnerTable && {
      '& .MuiTableCell-head': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.25,
        fontSize: 'var(--xtram-panel-head, 11px)',
        padding: '8px 6px',
        minWidth: 0,
      },
      '& .MuiTableCell-body': {
        fontSize: 'var(--xtram-panel-cell, 12px)',
        padding: '8px 6px',
        minWidth: 0,
      },
    }),
  };

  return (
    <Container
      dir={direction === 'rtl' ? 'ltr' : 'rtl'}
      component={Paper}
      className={isPartnerTable ? 'advances-table-container--partners' : ''}
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: isPartnerTable ? 'auto' : 'hidden',
      }}
    >
      {/* Subordinate Client Warning */}
      {isSubordinateClient && mainClientData && (
        <Box sx={{
          backgroundColor: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <Typography sx={{
            color: '#d32f2f',
            fontWeight: 'bold',
            fontSize: 'var(--xtram-text-sm, 14px)',
            marginBottom: '4px'
          }}>
            {t('SubordinateClientWarning')}
          </Typography>
          <Typography sx={{
            color: '#d32f2f',
            fontSize: 'var(--xtram-panel-cell, 12px)'
          }}>
            {t('ReportingWillBeDoneInMainCase')}: {mainClientData.clientName || mainClientData.name} ({mainClientData.caseId})
          </Typography>
        </Box>
      )}

      {/* Main Case with Subordinates Info */}
      {isMainWithSubordinates && subordinateClients.length > 0 && (
        <Box sx={{
          backgroundColor: '#fff3e0',
          border: '1px solid #ff9800',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <Typography sx={{
            color: '#e65100',
            fontWeight: 'bold',
            fontSize: 'var(--xtram-text-sm, 14px)',
            marginBottom: '4px'
          }}>
            {t('SharedCaseNotice') || 'תיק משותף - מקדמות מחושבות עבור כל התיקים'}
          </Typography>
          <Typography sx={{
            color: '#e65100',
            fontSize: 'var(--xtram-panel-cell, 12px)'
          }}>
            {t('IncludesSubordinateCases') || 'כולל תיקים משויכים'}: {subordinateClients.map(c => `${c.name || c.caseId} (${c.caseId})`).join(', ')}
          </Typography>
        </Box>
      )}
      <Table size="medium" sx={tableSx}>
        {/* ── header ── */}
        <TableHead>
          <TableRow>
            <HeadCell>{t('Payment')}</HeadCell>
            <HeadCell>{t('acceptedWithholdingTax')}</HeadCell>
            <HeadCell>{t(displayUsePercentage ? 'Advance' : 'FixedAdvance')}</HeadCell>
            {showAdvancePercentageColumn && (
              <HeadCell>{t('IncomeTaxAdvancePercentage')}</HeadCell>
            )}
            <HeadCell>{t('BusinessTurnoverInILS')}</HeadCell>
            {client.isPartners && <HeadCell>{t('Partner')}</HeadCell>}

            <HeadCell align="center">{t('Type')}</HeadCell>

          </TableRow>
        </TableHead>

        {/* ── body ── */}
        <TableBody>
          {client.isPartners && partners.length > 0 ? (
            // Show data based on selected partner
            (() => {
              if (selectedPartner) {
                // Show data for selected partner only
                const partner = selectedPartner;
                const partnerValues = partnerCalculatedValues[partner.id];

                // Use partner-specific calculated values if available, otherwise fallback to equity-based calculation
                const partnerComputedIncomeTax = partnerValues?.computed?.IncomeTaxAdvances || (calculatedValues.computed.IncomeTaxAdvances || 0) * (partner.equity / 100);
                const partnerComputedWithholding = partnerValues?.computed?.AcceptedWithholdingTaxMaximum || (calculatedValues.computed.AcceptedWithholdingTaxMaximum || 0) * (partner.equity / 100);
                const partnerComputedTotal = partnerValues?.computed?.total || (calculatedValues.computed.total || 0) * (partner.equity / 100);
                const partnerReportedIncomeTax = partnerValues?.reported?.IncomeTaxAdvances || (calculatedValues.reported.IncomeTaxAdvances || 0) * (partner.equity / 100);
                const partnerReportedWithholding = partnerValues?.reported?.AcceptedWithholdingTaxMaximum || (calculatedValues.reported.AcceptedWithholdingTaxMaximum || 0) * (partner.equity / 100);
                const partnerReportedTotal = partnerValues?.reported?.total || (calculatedValues.reported.total || 0) * (partner.equity / 100);
                const partnerWitholdingIncomes = partnerValues?.computed?.witholdingIncomesNoVat || (witholdingIncomesNoVat || 0) * (partner.equity / 100);
                const partnerReportedWitholdingIncomes = partnerValues?.reported?.witholdingIncomesNoVat || (advanceReportTax.witholdingIncomesNoVat || 0) * (partner.equity / 100);

                return (
                  <React.Fragment key={partner.id}>
                    {/* Computed Row for Selected Partner */}
                    <BodyRow>

                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerComputedIncomeTax, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerComputedWithholding, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerComputedTotal, 2, true)}
                        </span>
                      </NumberCell>
                      {showAdvancePercentageColumn && (
                      <NumberCell>
                        {(() => {
                          const { usePercentage, percentage, fixedAmount } = getPartnerAdvanceDisplay(partner);
                          return usePercentage
                            ? `${beautifyNumber(percentage)}%`
                            : `${beautifyNumber(fixedAmount, 2, true)} ₪`;
                        })()}
                      </NumberCell>
                      )}
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerWitholdingIncomes, 2, true)}
                        </span>
                      </NumberCell>

                      {client.isPartners && (
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {partner.name} ({partner.equity}%)
                          </span>
                        </NumberCell>
                      )}
                      <TableCell align="center">
                        <Tooltip title={t('DownloadAdvancesReport')}>
                          <Badge
                            label={t('Computed')}
                            palette="computed"
                            onClick={isSubordinateClient ? undefined : () => downloadWithholdingTaxReport(partner.id)}
                            clickable={!isSubordinateClient}
                          />
                        </Tooltip>
                      </TableCell>

                    </BodyRow>

                    {/* Reported Row for Selected Partner */}
                    <BodyRow>
                     
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerReportedIncomeTax, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerReportedWithholding, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerReportedTotal, 2, true)}
                        </span>
                      </NumberCell>
                      {showAdvancePercentageColumn && (
                      <NumberCell>
                        {(() => {
                          const { usePercentage, percentage, fixedAmount } = getPartnerAdvanceDisplay(partner);
                          return usePercentage
                            ? `${beautifyNumber(percentage)}%`
                            : `${beautifyNumber(fixedAmount, 2, true)} ₪`;
                        })()}
                      </NumberCell>
                      )}
                      <NumberCell>
                        <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {beautifyNumber(partnerReportedWitholdingIncomes, 2, true)}
                        </span>
                      </NumberCell>
                      {client.isPartners && <NumberCell></NumberCell>}
                      <TableCell align="center">
                        <Tooltip title={t('ReportAdvancesReport')}>
                          <span
                            onClick={isSubordinateClient ? undefined : () => {
                              if (
                                !permissionsState?.ViewSubAccountantClients &&
                                userTypeContext?.toString() !== Constants.UserType.Client
                              ) {
                                handleOpenWithholdingTaxModal(selectedPartner);
                              }
                            }}
                            style={{ cursor: isSubordinateClient ? 'not-allowed' : 'pointer' }}
                          >
                            <Badge label={t('Reported')} palette="reported" clickable />
                          </span>
                        </Tooltip>
                      </TableCell>
                    </BodyRow>

                    {/* Difference Row for Selected Partner */}
                    <BodyRow>
                      <NumberCell>
                        <span style={{
                          fontWeight: 600,
                          color: (partnerComputedIncomeTax - partnerReportedIncomeTax) >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {beautifyNumber(partnerComputedIncomeTax - partnerReportedIncomeTax, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{
                          color: (partnerComputedWithholding - partnerReportedWithholding) >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {beautifyNumber(partnerComputedWithholding - partnerReportedWithholding, 2, true)}
                        </span>
                      </NumberCell>
                      <NumberCell>
                        <span style={{
                          color: (partnerComputedTotal - partnerReportedTotal) >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {beautifyNumber(partnerComputedTotal - partnerReportedTotal, 2, true)}
                        </span>
                      </NumberCell>
                      {showAdvancePercentageColumn && (
                      <NumberCell>
                        {getPartnerAdvanceDisplay(partner).usePercentage
                          ? `${beautifyNumber(0)}%`
                          : `${beautifyNumber(0)} ₪`}
                      </NumberCell>
                      )}
                      <NumberCell>
                        <span style={{
                          color: (partnerWitholdingIncomes - partnerReportedWitholdingIncomes) >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {beautifyNumber(partnerWitholdingIncomes - partnerReportedWitholdingIncomes, 2, true)}
                        </span>
                      </NumberCell>
                      {client.isPartners && <NumberCell></NumberCell>}

                      <TableCell align="center">
                        <Badge label={t('Difference')} palette="difference" />
                      </TableCell>
                    </BodyRow>
                  </React.Fragment>
                );
              } else {

                // Show all partners (original logic)
                return partners.map((partner, partnerIndex) => {
                  const partnerValues = partnerCalculatedValues[partner.id];

                  // Use partner-specific calculated values if available, otherwise fallback to equity-based calculation
                  const partnerComputedIncomeTax = partnerValues?.computed?.IncomeTaxAdvances || (calculatedValues.computed.IncomeTaxAdvances || 0) * (partner.equity / 100);
                  const partnerComputedWithholding = partnerValues?.computed?.AcceptedWithholdingTaxMaximum || (calculatedValues.computed.AcceptedWithholdingTaxMaximum || 0) * (partner.equity / 100);
                  const partnerComputedTotal = partnerValues?.computed?.total || (calculatedValues.computed.total || 0) * (partner.equity / 100);
                  const partnerReportedIncomeTax = partnerValues?.reported?.IncomeTaxAdvances || (calculatedValues.reported.IncomeTaxAdvances || 0) * (partner.equity / 100);
                  const partnerReportedWithholding = partnerValues?.reported?.AcceptedWithholdingTaxMaximum || (calculatedValues.reported.AcceptedWithholdingTaxMaximum || 0) * (partner.equity / 100);
                  const partnerReportedTotal = partnerValues?.reported?.total || (calculatedValues.reported.total || 0) * (partner.equity / 100);
                  const partnerWitholdingIncomes = partnerValues?.computed?.witholdingIncomesNoVat || (witholdingIncomesNoVat || 0) * (partner.equity / 100);
                  const partnerReportedWitholdingIncomes = partnerValues?.reported?.witholdingIncomesNoVat || (advanceReportTax.witholdingIncomesNoVat || 0) * (partner.equity / 100);

                  return (
                    <React.Fragment key={partner.id}>
                      {/* Computed Row for Partner */}
                      <BodyRow>
                      
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerComputedIncomeTax, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerComputedWithholding, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerComputedTotal, 2, true)}
                          </span>
                        </NumberCell>
                        {showAdvancePercentageColumn && (
                        <NumberCell>
                          {(() => {
                            const { usePercentage, percentage, fixedAmount } = getPartnerAdvanceDisplay(partner);
                            return usePercentage
                              ? `${beautifyNumber(percentage)}%`
                              : `${beautifyNumber(fixedAmount, 2, true)} ₪`;
                          })()}
                        </NumberCell>
                        )}
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerWitholdingIncomes, 2, true)}
                          </span>
                        </NumberCell>
                        {client.isPartners && (
                          <NumberCell>
                            <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                              {partner.name} ({partner.equity}%)
                            </span>
                          </NumberCell>
                        )}
                        <TableCell align="center">
                          <Tooltip title={t('DownloadAdvancesReport')}>
                            <Badge
                              label={t('Computed')}
                              palette="computed"
                              onClick={isSubordinateClient ? undefined : handleDownloadWithPartnerSelection}
                              clickable={!isSubordinateClient}
                            />
                          </Tooltip>
                        </TableCell>
                      </BodyRow>

                      {/* Reported Row for Partner */}
                      <BodyRow>
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerReportedIncomeTax, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerReportedWithholding, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerReportedTotal, 2, true)}
                          </span>
                        </NumberCell>
                        {showAdvancePercentageColumn && (
                        <NumberCell>
                          {(() => {
                            const { usePercentage, percentage, fixedAmount } = getPartnerAdvanceDisplay(partner);
                            return usePercentage
                              ? `${beautifyNumber(percentage)}%`
                              : `${beautifyNumber(fixedAmount, 2, true)} ₪`;
                          })()}
                        </NumberCell>
                        )}
                        <NumberCell>
                          <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {beautifyNumber(partnerReportedWitholdingIncomes, 2, true)}
                          </span>
                        </NumberCell>
                        {client.isPartners && <NumberCell></NumberCell>}

                        <TableCell align="center">
                          <Tooltip title={t('ReportAdvancesReport')}>
                            <span
                              onClick={isSubordinateClient ? undefined : () => {
                                if (
                                  !permissionsState?.ViewSubAccountantClients &&
                                  userTypeContext?.toString() !== Constants.UserType.Client
                                ) {
                                  handleOpenWithholdingTaxModal(partner);
                                }
                              }}
                              style={{ cursor: isSubordinateClient ? 'not-allowed' : 'pointer' }}
                            >
                              <Badge label={t('Reported')} palette="reported" clickable />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </BodyRow>

                      {/* Difference Row for Partner */}
                      <BodyRow>
                      
                        <NumberCell>
                          <span style={{
                            fontWeight: 600,
                            color: (partnerComputedIncomeTax - partnerReportedIncomeTax) >= 0 ? '#2e7d32' : '#d32f2f'
                          }}>
                            {beautifyNumber(partnerComputedIncomeTax - partnerReportedIncomeTax, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{
                            color: (partnerComputedWithholding - partnerReportedWithholding) >= 0 ? '#2e7d32' : '#d32f2f'
                          }}>
                            {beautifyNumber(partnerComputedWithholding - partnerReportedWithholding, 2, true)}
                          </span>
                        </NumberCell>
                        <NumberCell>
                          <span style={{
                            color: (partnerComputedTotal - partnerReportedTotal) >= 0 ? '#2e7d32' : '#d32f2f'
                          }}>
                            {beautifyNumber(partnerComputedTotal - partnerReportedTotal, 2, true)}
                          </span>
                        </NumberCell>
                        {showAdvancePercentageColumn && (
                        <NumberCell>
                          {getPartnerAdvanceDisplay(partner).usePercentage
                            ? `${beautifyNumber(0)}%`
                            : `${beautifyNumber(0)} ₪`}
                        </NumberCell>
                        )}
                        <NumberCell>
                          <span style={{
                            color: (partnerWitholdingIncomes - partnerReportedWitholdingIncomes) >= 0 ? '#2e7d32' : '#d32f2f'
                          }}>
                            {beautifyNumber(partnerWitholdingIncomes - partnerReportedWitholdingIncomes, 2, true)}
                          </span>
                        </NumberCell>
                        {client.isPartners && <NumberCell></NumberCell>}
                        <TableCell align="center">
                          <Badge label={t('Difference')} palette="difference" />
                        </TableCell>
                      </BodyRow>
                    </React.Fragment>
                  );
                });
              }
            })()
          ) : (
            // Show regular client data (existing logic)
            <>
              {/* 1️⃣ Computed */}
              <BodyRow>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.computed.IncomeTaxAdvances || 0, 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.computed.AcceptedWithholdingTaxMaximum || 0, 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.computed.total || 0, 2, true)}
                  </span>
                </NumberCell>
                {showAdvancePercentageColumn && (
                <NumberCell>
                  {displayUsePercentage
                    ? `${beautifyNumber(displayAdvancePercentage)}%`
                    : `${beautifyNumber(displayAdvanceFixed, 2, true)} ₪`}
                </NumberCell>
                )}
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(witholdingIncomesNoVat || 0, 2, true)}
                  </span>
                </NumberCell>
                <TableCell align="center" >
                  <Tooltip title={t('DownloadAdvancesReport')}>
                    <Badge
                      label={t('Computed')}
                      palette="computed"
                      onClick={isSubordinateClient ? undefined : () => downloadWithholdingTaxReport(null)}
                      clickable={!isSubordinateClient}
                    />
                  </Tooltip>
                </TableCell>
              </BodyRow>

              {/* 2️⃣ Reported */}
              <BodyRow>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.reported.IncomeTaxAdvances || 0, 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.reported.AcceptedWithholdingTaxMaximum || 0, 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(calculatedValues.reported.total || 0, 2, true)}
                  </span>
                </NumberCell>
                {showAdvancePercentageColumn && (
                <NumberCell>
                  {displayUsePercentage
                    ? `${beautifyNumber(advanceReportTax?.incomeTaxAdvancePercentage)}%`
                    : `${beautifyNumber(advanceReportTax?.incomeTaxAdvanceAmount ?? 0, 2, true)} ₪`}
                </NumberCell>
                )}
                <NumberCell>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {beautifyNumber(advanceReportTax.witholdingIncomesNoVat || 0, 2, true)}
                  </span>
                </NumberCell>
                <TableCell align="center">
                  <Tooltip title={t('ReportAdvancesReport')}>
                    <span
                      onClick={isSubordinateClient ? undefined : () => {
                        if (
                          !permissionsState?.ViewSubAccountantClients &&
                          userTypeContext?.toString() !== Constants.UserType.Client
                        ) {
                          handleOpenWithholdingTaxModal(null);
                        }
                      }}
                      style={{ cursor: isSubordinateClient ? 'not-allowed' : 'pointer' }}
                    >
                      <Badge label={t('Reported')} palette="reported" clickable />
                    </span>
                  </Tooltip>
                </TableCell>
              </BodyRow>

              {/* 3️⃣ Difference */}
              <BodyRow>
                <NumberCell>
                  <span style={{
                    fontWeight: 600,
                    color: ((calculatedValues.computed.IncomeTaxAdvances || 0) - (calculatedValues.reported.IncomeTaxAdvances || 0)) >= 0 ? '#2e7d32' : '#d32f2f'
                  }}>
                    {beautifyNumber(
                      (calculatedValues.computed.IncomeTaxAdvances || 0) -
                      (calculatedValues.reported.IncomeTaxAdvances || 0)
                      , 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{
                    color: ((calculatedValues.computed.AcceptedWithholdingTaxMaximum || 0) - (calculatedValues.reported.AcceptedWithholdingTaxMaximum || 0)) >= 0 ? '#2e7d32' : '#d32f2f'
                  }}>
                    {beautifyNumber(
                      (calculatedValues.computed.AcceptedWithholdingTaxMaximum || 0) -
                      (calculatedValues.reported.AcceptedWithholdingTaxMaximum || 0)
                      , 2, true)}
                  </span>
                </NumberCell>
                <NumberCell>
                  <span style={{
                    color: ((calculatedValues.computed.total || 0) - (calculatedValues.reported.total || 0)) >= 0 ? '#2e7d32' : '#d32f2f'
                  }}>
                    {beautifyNumber(
                      (calculatedValues.computed.total || 0) -
                      (calculatedValues.reported.total || 0)
                      , 2, true)}
                  </span>
                </NumberCell>
                {showAdvancePercentageColumn && (
                <NumberCell>
                  {displayUsePercentage
                    ? `${beautifyNumber(
                      (displayAdvancePercentage || 0) -
                      (advanceReportTax?.incomeTaxAdvancePercentage || 0)
                    )}%`
                    : `${beautifyNumber(
                      (displayAdvanceFixed || 0) -
                        (advanceReportTax?.incomeTaxAdvanceAmount || 0),
                      2,
                      true,
                    )} ₪`}
                </NumberCell>
                )}
                <NumberCell>
                  <span style={{
                    color: ((witholdingIncomesNoVat || 0) - (advanceReportTax.witholdingIncomesNoVat || 0)) >= 0 ? '#2e7d32' : '#d32f2f'
                  }}>
                    {beautifyNumber(
                      (witholdingIncomesNoVat || 0) -
                      (advanceReportTax.witholdingIncomesNoVat || 0)
                      , 2, true)}
                  </span>
                </NumberCell>
                <TableCell align="center">
                  <Badge label={t('Difference')} palette="difference" />
                </TableCell>
              </BodyRow>
            </>
          )}
        </TableBody>
      </Table>
    </Container>
  );
};

/* ── prop types ── */
AdvancesTable.propTypes = {
  direction: PropTypes.string,
  headerStyle: PropTypes.object,
  t: PropTypes.func.isRequired,
  beautifyNumber: PropTypes.func.isRequired,
  client: PropTypes.object,
  calculatedValues: PropTypes.shape({
    computed: PropTypes.object,
    reported: PropTypes.object,
  }).isRequired,
  witholdingIncomesNoVat: PropTypes.number,
  downloadWithholdingTaxReport: PropTypes.func.isRequired,
  advanceReportTax: PropTypes.object,
  permissionsState: PropTypes.object,
  userTypeContext: PropTypes.any,
  handleOpenWithholdingTaxModal: PropTypes.func.isRequired,
  Constants: PropTypes.object,
  partners: PropTypes.array,
  selectedPartnerId: PropTypes.string,
  partnerCalculatedValues: PropTypes.object,
  isSubordinateClient: PropTypes.bool,
  mainClientData: PropTypes.object,
  isMainWithSubordinates: PropTypes.bool,
  subordinateClients: PropTypes.array,
};

export default AdvancesTable;